import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type OwnerOnboardingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface OwnerOnboardingState {
  status: OwnerOnboardingStatus;
  completedSections: {
    basicInformation: boolean; // Name, Email, Contact Number
  };
  lastUpdated?: string;
}

interface OwnerOnboardingContextType {
  onboardingState: OwnerOnboardingState;
  updateOnboardingSection: (section: keyof OwnerOnboardingState['completedSections'], isComplete: boolean) => void;
  isOnboardingComplete: boolean;
  getOnboardingProgress: () => { completed: number; total: number; percentage: number };
  forceCompleteOnboarding: () => void;
  resetOnboarding: () => void;
}

const OwnerOnboardingContext = createContext<OwnerOnboardingContextType | undefined>(undefined);

const STORAGE_KEY = 'petso_owner_onboarding_state';

export function OwnerOnboardingProvider({ children }: { children: ReactNode }) {
  const [onboardingState, setOnboardingState] = useState<OwnerOnboardingState>(() => {
    // Load from localStorage on init
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse owner onboarding state:', e);
      }
    }
    return {
      status: 'NOT_STARTED',
      completedSections: {
        basicInformation: false,
      },
    };
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(onboardingState));
  }, [onboardingState]);

  // Calculate if onboarding is complete
  const isOnboardingComplete = 
    onboardingState.completedSections.basicInformation;

  // Update status based on completed sections
  useEffect(() => {
    if (isOnboardingComplete && onboardingState.status !== 'COMPLETED') {
      setOnboardingState(prev => ({
        ...prev,
        status: 'COMPLETED',
        lastUpdated: new Date().toISOString(),
      }));
    } else if (!isOnboardingComplete && onboardingState.status === 'COMPLETED') {
      setOnboardingState(prev => ({
        ...prev,
        status: 'IN_PROGRESS',
        lastUpdated: new Date().toISOString(),
      }));
    }
  }, [isOnboardingComplete, onboardingState.status]);

  const updateOnboardingSection = (
    section: keyof OwnerOnboardingState['completedSections'],
    isComplete: boolean
  ) => {
    setOnboardingState(prev => {
      const hasAnySectionComplete = Object.values({
        ...prev.completedSections,
        [section]: isComplete,
      }).some(v => v);

      return {
        ...prev,
        completedSections: {
          ...prev.completedSections,
          [section]: isComplete,
        },
        status: hasAnySectionComplete && prev.status === 'NOT_STARTED' 
          ? 'IN_PROGRESS' 
          : prev.status,
        lastUpdated: new Date().toISOString(),
      };
    });
  };

  const getOnboardingProgress = () => {
    const sections = Object.values(onboardingState.completedSections);
    const completed = sections.filter(Boolean).length;
    const total = sections.length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  };

  const forceCompleteOnboarding = () => {
    setOnboardingState({
      status: 'COMPLETED',
      completedSections: {
        basicInformation: true,
      },
      lastUpdated: new Date().toISOString(),
    });
  };

  const resetOnboarding = () => {
    setOnboardingState({
      status: 'NOT_STARTED',
      completedSections: {
        basicInformation: false,
      },
      lastUpdated: new Date().toISOString(),
    });
  };

  return (
    <OwnerOnboardingContext.Provider
      value={{
        onboardingState,
        updateOnboardingSection,
        isOnboardingComplete,
        getOnboardingProgress,
        forceCompleteOnboarding,
        resetOnboarding,
      }}
    >
      {children}
    </OwnerOnboardingContext.Provider>
  );
}

export function useOwnerOnboarding() {
  const context = useContext(OwnerOnboardingContext);
  if (!context) {
    throw new Error('useOwnerOnboarding must be used within OwnerOnboardingProvider');
  }
  return context;
}
